import { useQuery } from "@tanstack/react-query";
import { Navigate, Outlet, Route, Routes } from "react-router-dom";

import { ApiError, api, type Me } from "./api";
import { AppShell } from "./components/AppShell";
import { Login } from "./routes/Login";
import { Order } from "./routes/Order";
import { PublicShell } from "./routes/PublicShell";
import { SetPassword } from "./routes/SetPassword";
import { Services } from "./routes/Services";
import { ServiceDetail } from "./routes/ServiceDetail";
import { AdminDiscovery } from "./routes/AdminDiscovery";
import { AdminClusters } from "./routes/AdminClusters";
import { DnsZones } from "./routes/DnsZones";
import { DnsZoneDetail } from "./routes/DnsZoneDetail";
import { Domains } from "./routes/Domains";
import { Websites } from "./routes/Websites";

function useMe() {
  return useQuery<Me, ApiError>({
    queryKey: ["me"],
    queryFn: () => api.get<Me>("/api/auth/me"),
    retry: (count, error) => !error.isUnauthenticated && count < 2,
    staleTime: 30_000,
  });
}

function Authenticated() {
  const { data: me, isPending, error } = useMe();

  if (isPending) {
    return (
      <div className="shell">
        <p className="muted" style={{ paddingTop: "4rem" }}>
          Loading…
        </p>
      </div>
    );
  }

  if (error?.isUnauthenticated || !me) return <Navigate to="/login" replace />;

  return (
    <AppShell me={me}>
      <Outlet context={me} />
    </AppShell>
  );
}

function StaffOnly({ children }: { children: React.ReactNode }) {
  const { data: me } = useMe();
  if (me && !me.is_staff) return <Navigate to="/services" replace />;
  return <>{children}</>;
}

/* Routes for a feature that is not finished yet.
 *
 * Hiding the nav link is not gating: a URL typed into the address bar reaches
 * the route regardless. This is what makes "the flag is off" a true statement
 * about reachability rather than only about discovery. */
function DomainsOnly({ children }: { children: React.ReactNode }) {
  const { data: me } = useMe();
  if (me && !me.features?.domains) return <Navigate to="/services" replace />;
  return <>{children}</>;
}

function WebsitesOnly({ children }: { children: React.ReactNode }) {
  const { data: me } = useMe();
  if (me && !me.features?.websites) return <Navigate to="/services" replace />;
  return <>{children}</>;
}

export function App() {
  return (
    <Routes>
      {/* Public, deliberately. Whoever follows a set-password link has no
       * session by definition — an account that has never had a password
       * cannot have one — so putting these inside <Authenticated> would
       * bounce every customer to /login and dead-end the provisioning
       * flow at its last step.
       *
       * <PublicShell> carries the branded background for both, mounted once
       * so moving between them does not restart the scene. */}
      <Route element={<PublicShell />}>
        <Route path="/login" element={<Login />} />
        <Route path="/set-password" element={<SetPassword />} />
      </Route>
      <Route element={<Authenticated />}>
        <Route index element={<Navigate to="/services" replace />} />
        <Route path="/services" element={<Services />} />
        <Route path="/services/new" element={<Order />} />
        <Route path="/services/:serviceId" element={<ServiceDetail />} />
        <Route
          path="/dns"
          element={
            <DomainsOnly>
              <DnsZones />
            </DomainsOnly>
          }
        />
        <Route
          path="/dns/:zoneId"
          element={
            <DomainsOnly>
              <DnsZoneDetail />
            </DomainsOnly>
          }
        />
        <Route
          path="/websites"
          element={
            <WebsitesOnly>
              <Websites />
            </WebsitesOnly>
          }
        />
        <Route
          path="/domains"
          element={
            <DomainsOnly>
              <Domains />
            </DomainsOnly>
          }
        />
        <Route
          path="/admin/discovery"
          element={
            <StaffOnly>
              <AdminDiscovery />
            </StaffOnly>
          }
        />
        <Route
          path="/admin/clusters"
          element={
            <StaffOnly>
              <AdminClusters />
            </StaffOnly>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/services" replace />} />
    </Routes>
  );
}
